'use client'

import { Fragment, useMemo, useState, useEffect } from 'react'

import { useAppSearchParams } from '@/shared/lib/router'

import { markRankingGaps } from '../../domain/markRankingGaps'
import { resolveCampaignRankingRows } from '../../domain/resolveCampaignRankingRows'
import { useRankingStore } from '../../stores/useRankingStore'
import { RankingCard } from '../RankingCard'
import { RankingCardSkeleton } from '../RankingCardSkeleton'
import { RankingGapDivider } from '../RankingGapDivider'

import type { UserGroup } from '@/features/groups/types/groups'
import { useCampaignRanking } from '@/features/campaigns/hooks/useCampaignRanking'
import type {
  Campaign,
  CampaignRankingScope
} from '@/features/campaigns/types/campaign'
import { UserModal } from '@/features/profiles'
import { useModalContext } from '@/shared/contexts/Modal'
import { useUserProgress } from '@/shared/contexts/UserProgress'
import { useCurrentUser } from '@/shared/hooks/useCurrentUser'
import { useOptimizedRecords } from '@/shared/hooks/useOptimizedQuery'
import { useRankingAnalytics } from '@/shared/lib/analytics'
import { RankingUser } from '@/shared/types/user'
import { Card, CardBody, CardHeader, CardTitle } from '@/shared/ui/Card'
import { selectClassName } from '@/shared/ui/input-styles'
import { NotFoundAlert } from '@/shared/ui/NotFoundAlert'
import { Page } from '@/shared/ui/Page'
import { Pagination } from '@/shared/ui/Pagination'
import { Tabs, Tab } from '@/shared/ui/Tabs'

export function RankingListPage() {
  const searchParams = useAppSearchParams()
  const { ranking, isLoading } = useRankingStore()
  const tabParam = searchParams.get('tab')
  const groupParam = searchParams.get('group')
  const [selectedTab, setSelectedTab] = useState(
    tabParam === 'group' ? 'group' : 'global'
  )
  const [selectedSchoolId, setSelectedSchoolId] = useState<number | null>(null)
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(
    groupParam ? Number(groupParam) : null
  )
  const [selectedCampaignId, setSelectedCampaignId] = useState<number | null>(null)
  const [campaignScope, setCampaignScope] =
    useState<CampaignRankingScope>('global')
  const [campaignPage, setCampaignPage] = useState(1)
  const { openModal } = useModalContext()
  const { trackRankingViewed, trackUserRankClicked } = useRankingAnalytics()
  const { isAdmin } = useCurrentUser()
  const { campaignParticipants, campaignLinks } = useUserProgress()

  // Link pelo qual o usuário entrou na campanha selecionada — rotula a aba
  // do ranking por link com o código do próprio link.
  const myCampaignLink = useMemo(() => {
    if (!selectedCampaignId) return null
    const participant = campaignParticipants.find(
      p => p.campaign_id === selectedCampaignId
    )
    if (!participant) return null
    return (
      campaignLinks.find(link => link.id === participant.campaign_link_id) ??
      null
    )
  }, [campaignParticipants, campaignLinks, selectedCampaignId])

  interface School {
    id: number
    name: string
  }

  const { records: schools, isLoading: isLoadingSchools } =
    useOptimizedRecords<School>({
      path: '/admin/schools',
      enabled: isAdmin && selectedTab === 'schools'
    })

  const { records: schoolRanking, isLoading: isLoadingSchoolRanking } =
    useOptimizedRecords<RankingUser>({
      path: '/admin/schools/ranking',
      params: { school_ids: selectedSchoolId ? [selectedSchoolId] : [] },
      enabled: isAdmin && selectedTab === 'schools' && !!selectedSchoolId
    })

  const { records: myGroups } = useOptimizedRecords<UserGroup>({
    path: '/user/groups',
    enabled: selectedTab === 'group'
  })

  const { records: groupRanking, isLoading: isLoadingGroupRanking } =
    useOptimizedRecords<RankingUser>({
      path: `/user/groups/${selectedGroupId}/ranking`,
      enabled: selectedTab === 'group' && !!selectedGroupId
    })

  const { records: myCampaigns } = useOptimizedRecords<Campaign>({
    path: '/user/campaigns',
    enabled: selectedTab === 'campaign'
  })

  const {
    records: campaignRanking,
    currentUser: campaignCurrentUser,
    campaignLink: rankedCampaignLink,
    pagination: campaignPagination,
    isLoading: isLoadingCampaignRanking
  } = useCampaignRanking(selectedCampaignId!, campaignScope, {
    page: campaignPage,
    enabled: selectedTab === 'campaign' && !!selectedCampaignId
  })

  const {
    rows: campaignRows,
    currentUserRow: campaignCurrentUserRow,
    hasGap: hasCampaignRankGap
  } = useMemo(
    () => resolveCampaignRankingRows(campaignRanking, campaignCurrentUser),
    [campaignRanking, campaignCurrentUser]
  )

  const filteredRanking = useMemo(() => {
    return ranking?.[selectedTab as keyof typeof ranking] ?? []
  }, [ranking, selectedTab])

  useEffect(() => {
    if (!isLoading && filteredRanking.length > 0) {
      trackRankingViewed(selectedTab as 'global' | 'school' | 'friends')
    }
  }, [selectedTab, filteredRanking.length, isLoading, trackRankingViewed])

  // Pré-seleciona a primeira campanha ao abrir a aba (não há mais dropdown).
  useEffect(() => {
    if (
      selectedTab === 'campaign' &&
      !selectedCampaignId &&
      myCampaigns &&
      myCampaigns.length > 0
    ) {
      setSelectedCampaignId(myCampaigns[0].id)
    }
  }, [selectedTab, selectedCampaignId, myCampaigns])

  const handleTabChange = (value: string) => {
    setSelectedTab(value)
    if (value !== 'group') setSelectedGroupId(null)
    if (value !== 'campaign') {
      setSelectedCampaignId(null)
      setCampaignScope('global')
      setCampaignPage(1)
    }
  }

  const handleShowUserProfile = (rankingUser: RankingUser) => {
    trackUserRankClicked(rankingUser)
    openModal(
      {
        modal: UserModal
      },
      {
        userUid: rankingUser.user.uid
      }
    )
  }

  const renderContent = () => {
    if (selectedTab === 'schools') {
      return (
        <div className="flex flex-col gap-3">
          <div className="w-full">
            <select
              className={selectClassName}
              value={selectedSchoolId ?? ''}
              onChange={e =>
                setSelectedSchoolId(
                  e.target.value ? Number(e.target.value) : null
                )
              }
              disabled={isLoadingSchools}
            >
              <option value="">Selecione uma escola</option>
              {schools?.map(school => (
                <option key={school.id} value={school.id}>
                  {school.name}
                </option>
              ))}
            </select>
          </div>

          {isLoadingSchoolRanking && (
            <div className="flex flex-col gap-2">
              <RankingCardSkeleton />
              <RankingCardSkeleton />
              <RankingCardSkeleton />
            </div>
          )}

          {!isLoadingSchoolRanking && selectedSchoolId && (
            <>
              {(!schoolRanking || schoolRanking.length === 0) && (
                <NotFoundAlert
                  title="Ninguém por aqui 😔"
                  text="Ainda não há ninguém no ranking desta escola. Que tal ser o primeiro?"
                />
              )}

              {schoolRanking?.map(rankingUser => (
                <RankingCard
                  key={rankingUser.user.uid}
                  rankingUser={rankingUser}
                  onClick={() => handleShowUserProfile(rankingUser)}
                />
              ))}
            </>
          )}
        </div>
      )
    }

    if (selectedTab === 'campaign') {
      return (
        <div className="flex flex-col gap-3">
          {myCampaigns && myCampaigns.length > 0 && (
            <Tabs
              value={selectedCampaignId ? String(selectedCampaignId) : ''}
              onValueChange={value => {
                setSelectedCampaignId(value ? Number(value) : null)
                setCampaignPage(1)
              }}
              className="justify-center sm:flex-wrap"
            >
              {myCampaigns.map(campaign => (
                <Tab key={campaign.id} value={String(campaign.id)}>
                  {campaign.name}
                </Tab>
              ))}
            </Tabs>
          )}

          {selectedCampaignId && (
            <Tabs
              value={campaignScope}
              onValueChange={value => {
                setCampaignScope(value as CampaignRankingScope)
                setCampaignPage(1)
              }}
              className="justify-center"
              visibleCountMobile={3}
              visibleCountDesktop={3}
            >
              <Tab value="global">Geral</Tab>
              <Tab value="friends">Amigos</Tab>
              <Tab value="link">{myCampaignLink?.code ?? 'Meu link'}</Tab>
            </Tabs>
          )}

          {campaignScope === 'link' && rankedCampaignLink && (
            <p className="text-center text-sm font-semibold text-[#7F7FAD]">
              {rankedCampaignLink.participants_count} participante
              {rankedCampaignLink.participants_count === 1
                ? ' entrou'
                : 's entraram'}{' '}
              por este link
            </p>
          )}

          {isLoadingCampaignRanking && (
            <div className="flex flex-col gap-2">
              <RankingCardSkeleton />
              <RankingCardSkeleton />
              <RankingCardSkeleton />
            </div>
          )}

          {!isLoadingCampaignRanking && selectedCampaignId && (
            <>
              {campaignRows.length === 0 && !campaignCurrentUserRow && (
                <NotFoundAlert
                  title="Ninguém por aqui 😔"
                  text="Ainda não há ninguém no ranking desta campanha."
                />
              )}

              {campaignRows.map(row => (
                <RankingCard
                  key={row.user_id}
                  rankingUser={row as unknown as RankingUser}
                  onClick={() => {}}
                />
              ))}

              {campaignCurrentUserRow && (
                <>
                  {hasCampaignRankGap && <RankingGapDivider />}
                  <RankingCard
                    key={campaignCurrentUserRow.user_id}
                    rankingUser={campaignCurrentUserRow as unknown as RankingUser}
                    isCurrentUser
                    onClick={() => {}}
                  />
                </>
              )}

              {campaignScope === 'link' &&
              campaignPagination &&
              campaignPagination.pages > 1 ? (
                <Pagination
                  currentPage={campaignPagination.page}
                  pageCount={campaignPagination.pages}
                  totalCount={campaignPagination.count}
                  onPrev={() => setCampaignPage(p => Math.max(1, p - 1))}
                  onNext={() => setCampaignPage(p => p + 1)}
                  ariaLabel="Paginação do ranking do link"
                />
              ) : null}
            </>
          )}

          {!selectedCampaignId && myCampaigns?.length === 0 && (
            <NotFoundAlert
              title="Nenhuma campanha"
              text="Você não participa de nenhuma campanha ainda."
            />
          )}
        </div>
      )
    }

    if (selectedTab === 'group') {
      return (
        <div className="flex flex-col gap-3">
          <div className="w-full">
            <select
              className={selectClassName}
              value={selectedGroupId ?? ''}
              onChange={e =>
                setSelectedGroupId(
                  e.target.value ? Number(e.target.value) : null
                )
              }
            >
              <option value="">Selecione um grupo</option>
              {myGroups?.map(group => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </select>
          </div>

          {isLoadingGroupRanking && (
            <div className="flex flex-col gap-2">
              <RankingCardSkeleton />
              <RankingCardSkeleton />
              <RankingCardSkeleton />
            </div>
          )}

          {!isLoadingGroupRanking && selectedGroupId && (
            <>
              {(!groupRanking || groupRanking.length === 0) && (
                <NotFoundAlert
                  title="Ninguém por aqui 😔"
                  text="Ainda não há ninguém no ranking deste grupo."
                />
              )}

              {markRankingGaps(groupRanking ?? []).map(
                ({ row, hasGapBefore }) => (
                  <Fragment key={row.user.uid}>
                    {hasGapBefore && <RankingGapDivider />}
                    <RankingCard
                      rankingUser={row}
                      isCurrentUser={row.is_current_user}
                      onClick={() => handleShowUserProfile(row)}
                    />
                  </Fragment>
                )
              )}
            </>
          )}

          {!selectedGroupId && myGroups?.length === 0 && (
            <NotFoundAlert
              title="Nenhum grupo"
              text="Você não participa de nenhum grupo ainda. Entre em um grupo pelo código para ver o ranking."
            />
          )}
        </div>
      )
    }

    if (isLoading) {
      return (
        <div className="flex flex-col gap-2">
          <RankingCardSkeleton />
          <RankingCardSkeleton />
          <RankingCardSkeleton />
        </div>
      )
    }

    if (filteredRanking.length === 0) {
      return (
        <NotFoundAlert
          title="Ninguém por aqui 😔"
          text="Ainda não há ninguém no ranking desta categoria. Que tal ser o primeiro?"
        />
      )
    }

    // Geral, Escola e Amigos já vêm do backend com a linha do usuário logado
    // anexada ao topo da lista — aqui só falta destacá-la e sinalizar a lacuna.
    return markRankingGaps(filteredRanking).map(({ row, hasGapBefore }) => (
      <Fragment key={row.user.uid}>
        {hasGapBefore && <RankingGapDivider />}
        <RankingCard
          rankingUser={row}
          isCurrentUser={row.is_current_user}
          onClick={() => handleShowUserProfile(row)}
        />
      </Fragment>
    ))
  }

  return (
    <Page variant="yellow">
      <div className="flex flex-col gap-4 w-full items-center min-w-0 max-w-full px-2 sm:px-0">
        <Card variant="yellow" showTrophies className="w-full max-w-2xl">
          <CardHeader>
            <CardTitle>Ranking</CardTitle>
          </CardHeader>
          <CardBody>
            <Tabs
              value={selectedTab}
              onValueChange={handleTabChange}
              className="justify-center"
              visibleCountMobile={3}
              visibleCountDesktop={3}
            >
              <Tab value="global">Geral</Tab>
              <Tab value="school">Escola</Tab>
              <Tab value="friends">Amigos</Tab>
              {isAdmin && <Tab value="schools">Por escola</Tab>}
              <Tab value="campaign">Por campanha</Tab>
              <Tab value="group">Por grupo</Tab>
            </Tabs>

            <div className="flex flex-col gap-2">{renderContent()}</div>
          </CardBody>
        </Card>
      </div>
    </Page>
  )
}
